
<template>
  <TransitionRoot as="template" :show="isPurchaseModalOpen">
      <Dialog as="div" auto-reopen="true" class="fixed z-10 inset-0 overflow-y-auto" @close="this.closePurchaseModal()">
        <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
            <DialogOverlay class="fixed inset-0 dark:bg-true-gray-900 dark:bg-opacity-75 bg-warm-gray-900 bg-opacity-75" />
          </TransitionChild>
          <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
            &#8203;
          </span>
          
          <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
            <div class="inline-block align-bottom dark:bg-true-gray-800 bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full sm:p-6">
              <!-- Close Button -->
              <div v-if="this.slide_index != 4" class="block absolute top-0 right-0 pt-4 pr-4">
                <button type="button" class="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none" @click="this.closePurchaseModal()">
                  <span class="sr-only">Close</span>
                  <XIcon class="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              <!-- Slides -->
              <div class="relative px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto py-4 mt-4">

                <!-- Updated -->
                <img class="hidden" :src="ORIG_PRICE_SC" rel="preload" />
                <img class="hidden" :src="DISC_PRICE_SC" rel="preload" />
                <img class="hidden" :src="DISC_PRICE_SC_1K_LESS" rel="preload" />

                <img class="hidden" :src="ORIG_PRICE_SC_PROOF" rel="preload" />
                <img class="hidden" :src="DISC_PRICE_SC_PROOF" rel="preload" />
                <img class="hidden" :src="DISC_PRICE_SC_PROOF_1K_LESS" rel="preload" />

                <!-- STEP 1 -->
                <div v-if="this.slide_index == 0" class="dark:text-gray-400 text-gray-600 leading-6 text-md max-w-prose mx-auto">
                  <span class="block text-base text-center dark:text-yellow-300 text-yellow-500 font-semibold tracking-wide uppercase">{{ modalTitle }}</span>
                  <h1 class="text-2xl tracking-tight font-extrabold dark:text-gray-300 text-gray-700 md:text-3xl my-6">
                    <span class="block xl:inline">Instructions for Payment via GCash</span>
                  </h1>
                  <p>
                    You will be guided through the steps for purchasing the CAAP reviewer. Here's a summary before we proceed:
                  </p>
                  <div class="flex items-start my-4 space-x-4 ">
                    <div class="mx-auto font-bold flex-shrink-0 flex items-center dark:text-gray-800 justify-center h-12 w-12 rounded-full bg-yellow-400 sm:mx-0 sm:h-10 sm:w-10">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                      </svg>
                    </div>
                    <p>
                      Scan our GCash QR Code to make the payment. <span v-if="this.isCouponValid">The <strong>coupon discount</strong> should already be applied and you will see the total amount of PHP {{ this.data.finalPrice.toLocaleString() }} automatically.</span>
                    </p>
                  </div>
                  
                  <div class="flex items-start my-4 space-x-4 my-8">
                    <div class="mx-auto flex-shrink-0 flex items-center dark:text-gray-800 justify-center h-12 w-12 rounded-full bg-yellow-400 sm:mx-0 sm:h-10 sm:w-10 mt-2">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                      </svg>
                    </div>
                    <p>
                      Once payment is made, take a <strong>screenshot</strong> of the successful transaction in your GCash app for your copy and input the <strong>Ref. No. of your receipt</strong>. Click the <strong>"Payment Sent!"</strong> button once done. 
                    </p>
                  </div>
                  <div class="flex items-start my-4 space-x-4 my-8">
                    <div class="mx-auto flex-shrink-0 flex items-center dark:text-gray-800 justify-center h-12 w-12 rounded-full bg-yellow-400 sm:mx-0 sm:h-10 sm:w-10 mt-2">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M3 19v-8.93a2 2 0 01.89-1.664l7-4.666a2 2 0 012.22 0l7 4.666A2 2 0 0121 10.07V19M3 19a2 2 0 002 2h14a2 2 0 002-2M3 19l6.75-4.5M21 19l-6.75-4.5M3 10l6.75 4.5M21 10l-6.75 4.5m0 0l-1.14.76a2 2 0 01-2.22 0l-1.14-.76" />
                      </svg>
                    </div>
                    <p>
                      CAAP access will be {{ this.data.activateLater ? 'ready for activation' : 'granted'}}  after payment is received and confirmed within 24 hours. Expect an email confirmation also within 24 hours after purchase.
                    </p>
                  </div>
                  <button @click="this.nextStep(1)" class="mt-2 text-white bg-indigo-600 w-full py-4 border border-transparent rounded-md shadow-sm px-4 inline-flex justify-center text-xl font-medium hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-30 disabled:pointer-events-none disabled:cursor-auto">
                    Continue
                  </button>    
                </div>

                <!-- STEP 2 -->
                <div v-if="this.slide_index == 1" class="dark:text-gray-400 text-gray-600 leading-6 text-md max-w-prose mx-auto">
                  <span class="block text-base text-center dark:text-yellow-300 text-yellow-500 font-semibold tracking-wide uppercase">{{ modalTitle }}</span>
                  <h1 class="text-2xl tracking-tight font-extrabold dark:text-gray-300 text-gray-700 md:text-3xl my-6">
                    <span class="dark:text-yellow-200 text-yellow-400">Step 1:</span>{{ ' ' }}
                    <span class="">Scan the QR Code</span>
                  </h1>

                  <!-- <div :class="[(!isLoaded) ? 'animate-pulse' : '', 'rounded-xl bg-white w-auto my-8']">
                    <img v-if="isCouponValid && data.couponDiscount == 1000" class="mx-auto w-auto" @load="isLoaded = true" :src="DISC_PRICE_QR_1K_LESS" rel="preload" />
                    <img v-else-if="isCouponValid" class="mx-auto w-auto" @load="isLoaded = true" :src="DISC_PRICE_QR" rel="preload" />
                    <img v-else class="mx-auto w-auto" @load="isLoaded = true" :src="ORIG_PRICE_QR" rel="preload" />
                  </div> -->
                  <div :class="[(isFetchingQRCodes) ? 'animate-pulse' : '', 'rounded-xl bg-white w-auto my-8']">

                    <div v-if="isFetchingQRCodes" class="mx-auto h-80 bg-white animate-pulse flex justify-center items-center">
                      <p>Fetching QR Code. Please wait.</p>
                    </div>
                    <div v-else>
                      <img v-if="isCouponValid && data.couponDiscount == 1000" class="mx-auto w-auto" :src="DISC_PRICE_QR_1K_LESS" rel="preload" />
                      <img v-else-if="isCouponValid" class="mx-auto w-auto" :src="DISC_PRICE_QR" rel="preload" />
                      <img v-else class="mx-auto w-auto" :src="ORIG_PRICE_QR" rel="preload" />
                    </div>

                  </div>

                  <p>
                    Open your GCash App and scan the QR code above. This should take you to the GCash Express Send page where the payment details are already populated. If there are any issues with the QR Code, you can send the <strong class="text-yellow-500 dark:text-yellow-400">PHP {{ this.data.finalPrice.toLocaleString() }}</strong> payment to GCash number <strong class="text-yellow-500 dark:text-yellow-400" :class="isFetchingQRCodes ? 'animate-pulse opacity-20':''">{{ isFetchingQRCodes ? '09XXXXXXXXX' : gcashPhoneNum }}</strong>, or contact your Endorser for further assistance. Click the button below once done. 
                  </p>
                  <button @click="this.nextStep(2)" class="mt-8 text-white bg-indigo-600 w-full py-4 border border-transparent rounded-md shadow-sm px-4 inline-flex justify-center text-xl font-medium hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-30 disabled:pointer-events-none disabled:cursor-auto">
                    Proceed To Next Step
                  </button>
                  <div class="mt-4 text-center w-full text-gray-700 hover:text-gray-500 dark:text-gray-200 dark:hover:text-gray-300 mx-auto text-sm font-medium">
                    <button @click="this.nextStep(0)" >Go back</button>
                  </div>
                </div>

                <!-- Next Step -->
                <div v-if="this.slide_index == 2" class="dark:text-gray-400 text-gray-600 leading-6 text-md max-w-prose mx-auto">
                  <span class="block text-base text-center dark:text-yellow-300 text-yellow-500 font-semibold tracking-wide uppercase">{{ modalTitle }}</span>

                  <div v-if="!didNotPayThruGcash">

                    <h1 class="text-2xl tracking-tight font-extrabold dark:text-gray-300 text-gray-700 md:text-3xl my-6">
                      <span class="dark:text-yellow-200 text-yellow-400">Step 2:</span>{{ ' ' }}
                      <span>Enter your Reference Number below</span>
                    </h1>

                    <!-- Show normal original price screenshot if coupon is not valid -->
                    <img v-if="isCouponValid && data.couponDiscount == 1000" class="mx-auto w-auto" @load="isLoaded = true" :src="DISC_PRICE_SC_1K_LESS" rel="preload" />
                    <img v-else-if="isCouponValid" class="mx-auto w-auto" @load="isLoaded = true" :src="DISC_PRICE_SC" rel="preload" />
                    <img v-else class="mx-auto w-auto" @load="isLoaded = true" :src="ORIG_PRICE_SC" rel="preload" />
                    
                    <p class="mt-4">
                      Don't forget to take a <strong class="text-yellow-500 dark:text-yellow-400">screenshot</strong> of the successful transaction in your GCash app for your copy and save it. Input the <strong class="text-yellow-500 dark:text-yellow-400">Ref. No. of your receipt</strong> below (see sample screenshot above). Once done, click the <strong>"Payment Sent!"</strong> button below to complete the payment. 
                    </p>
                    <div class="mt-4">
                      <input v-model="refno" type="text" name="refno" id="refno" class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-200 border-2 rounded-lg p-4" placeholder="Enter your 13-digit Reference Number (i.e. Ref. No.) here"/>
                    </div>

                  </div>

                  <div v-else>

                    <h1 class="text-2xl tracking-tight font-extrabold dark:text-gray-300 text-gray-700 md:text-3xl my-6">
                      <span class="dark:text-yellow-200 text-yellow-400">Step 2:</span>{{ ' ' }}
                      <span>Upload the proof of payment</span>
                    </h1>

                    <img v-if="isCouponValid && data.couponDiscount == 1000" class="mx-auto w-auto" @load="isLoaded = true" :src="DISC_PRICE_SC_PROOF_1K_LESS" rel="preload" />
                    <img v-else-if="isCouponValid" class="mx-auto w-auto" @load="isLoaded = true" :src="DISC_PRICE_SC_PROOF" rel="preload" />
                    <img v-else class="mx-auto w-auto" @load="isLoaded = true" :src="ORIG_PRICE_SC_PROOF" rel="preload" />

                    <p>
                      Don't forget to take a <strong class="text-yellow-500 dark:text-yellow-400">screenshot</strong> of your successful payment transaction and save it. Upload the <strong class="text-yellow-500 dark:text-yellow-400">proof of payment</strong> image below (see the sample screenshot above). Once you're done, click the <strong>"Payment Sent!"</strong> button to complete your payment.
                    </p>


                    <div class="mt-4">
                      <!-- Preview Section -->
                      <div v-if="paymentImagePreview" class="mt-4 flex justify-start items-end p-2 gap-4">
                        <img :src="paymentImagePreview" class=" max-h-24" />
                        <div class="">
                          <p class=" text-sm">{{ paymentImage.name }}</p>
                          <button @click="removeSelectedImage" class="bg-red-600 text-white text-xs px-2 py-1 rounded">
                            Remove
                          </button>
                        </div>
                      </div>
                       <label v-else for="imageUpload" class="cursor-pointer border rounded-md p-2 dark:text-yellow-300 text-yellow-600 dark:border-yellow-300 border-yellow-600 bg-yellow-500 bg-opacity-10 w-full flex flex-col items-center justify-center">
                        <p>Click to upload image</p>
                        <p class="text-xs opacity-80">Make sure to upload a clear copy.</p>
                        <input
                          id="imageUpload"
                          type="file"
                          accept="image/*"
                          @change="onImageSelected"
                          class="hidden"
                        />
                      </label>
                    </div>


                  </div>

                  <div class="flex gap-3 mt-4">
                    <div class="flex h-6 shrink-0 items-center">
                      <div class="group grid size-4 grid-cols-1">
                        <input
                          v-model="didNotPayThruGcash"
                          id="not-use-gcash"
                          aria-describedby="not-use-gcash-description"
                          name="not-use-gcash"
                          type="checkbox"
                          class="col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-indigo-600 checked:bg-indigo-600 indeterminate:border-indigo-600 indeterminate:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                        />
                        <svg
                          class="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-[:disabled]:stroke-gray-950/25"
                          viewBox="0 0 14 14"
                          fill="none"
                        >
                          <path
                            class="opacity-0 group-has-[:checked]:opacity-100"
                            d="M3 8L6 11L11 3.5"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <path
                            class="opacity-0 group-has-[:indeterminate]:opacity-100"
                            d="M3 7H11"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                      </div>
                    </div>
                    <div class="text-sm/6">
                      <label for="not-use-gcash" class="font-medium dark:text-gray-200">I did not use GCash.</label>
                      {{ ' ' }}
                      <span id="not-use-gcash-description" class="text-gray-400 italic">( I used other payment method. )</span>
                    </div>
                  </div>

                  <button :disabled="!isRefNoValid || !hasPaymentImage" @click="this.prepareToOnboard()" class="disabled:bg-gray-400 mt-8 text-white bg-indigo-600 w-full py-4 border border-transparent rounded-md shadow-sm px-4 inline-flex justify-center text-xl font-medium hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-30 disabled:pointer-events-none disabled:cursor-auto">
                    Payment Sent! 
                  </button>
                  <div class="mt-4 text-center w-full text-gray-700 hover:text-gray-500 dark:text-gray-200 dark:hover:text-gray-300 mx-auto text-sm font-medium">
                    <button @click="this.nextStep(1)" >Go back</button>
                  </div>           
                </div>

                <!-- STEP 5 -->
                <div v-if="this.slide_index == 4" class="dark:text-gray-400 text-gray-600 leading-6 text-md max-w-prose mx-auto">
                  <span class="block text-base text-center dark:text-yellow-300 text-yellow-500 font-semibold tracking-wide uppercase">{{ modalTitle }}</span>
                  <h1 class="text-2xl tracking-tight font-extrabold dark:text-gray-300 text-gray-700 md:text-3xl my-6">
                    <span v-if="!readyToExit" class="block xl:inline">One moment please...</span>
                    <span v-else class="block xl:inline">Awesome! <span class="dark:text-yellow-200 text-yellow-400">Thank You</span> for your purchase.</span>
                  </h1>
                  <p>
                    We are excited for you to start your review! Your CAAP access will be {{ this.data.activateLater ? 'ready for activation' : 'granted' }} after payment is received and confirmed within 24 hours. Expect an email once confirmed. If you have any concerns, please reach out to us via email at <strong>support@skilltech.ph</strong> or through our Facebook Messenger.
                  </p>

                  <div v-if="!readyToExit" class="relative pt-1 mt-8">
                    <div class="overflow-hidden h-2 mb-4 text-xs flex rounded bg-yellow-200">
                        <div
                        :style="`width: ${progress}%`"
                        class="transition-all duration-500 ease-in-out shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-yellow-500"
                        ></div>
                    </div>
                  </div>
                  
                  <div class="bg-yellow-50 overflow-hidden rounded-lg text-warm-gray-500 text-left mt-8 max-w-full">
                    <div class="px-4 py-5 sm:py-8 sm:px-9 flex space-x-2 items-start">
                      <div>
                        <LightBulbIcon class="text-yellow-400 w-5 h-5" />
                      </div>
                      <p class="text-yellow-700">
                        <strong>Tip:</strong> {{tips[current]}}
                      </p>
                    </div>
                  </div>
                  
                  <button @click="this.closePurchaseModal()" :disabled="!readyToExit" class="mt-8 text-white bg-indigo-600 w-full py-4 border border-transparent rounded-md shadow-sm px-4 inline-flex justify-center text-lg font-medium hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:pointer-events-none disabled:cursor-auto">
                    Close
                  </button>
                </div>

              </div>
            </div>
          </TransitionChild>
        </div>
      </Dialog>
    </TransitionRoot>
</template>
 
<script>

import moment from "moment";
import firebase from "@/firebase/config";
// import "firebase/functions";
import "firebase/auth";
import "firebase/firestore";
const db = firebase.firestore();

import { LightBulbIcon, XIcon } from "@heroicons/vue/solid";
import { homeData } from "@/data/home.data"

import { useCoupon } from '@/views/coupon/couponService'

import {
  TransitionRoot,
  TransitionChild,
  Dialog,
  DialogOverlay,
  DialogTitle,
} from "@headlessui/vue";

const functions = firebase.functions();

// Search for 'switch-gcash-code' and the files that need to be updated will be shown.
/******************
 * John QR Codes
 */
const john_PHONE_NUM = "09062846028";
/** 6 Months */
import john_qr5999 from '@/assets/images/qr/QR-5999.png';
import john_qr5799 from '@/assets/images/qr/QR-5799.png';
import john_qr4999 from '@/assets/images/qr/QR-4999.png';
/** 3 Months */
import john_qr3999 from '@/assets/images/qr/QR-3999.png';
import john_qr3799 from '@/assets/images/qr/QR-3799.png';
import john_qr2999 from '@/assets/images/qr/QR-2999.png';
/** 1 Month */
import john_qr1999 from '@/assets/images/qr/QR-1999.png';
import john_qr1799 from '@/assets/images/qr/QR-1799.png';
import john_qr999 from '@/assets/images/qr/QR-999.png';


/******************
 * Veen QR Codes
 */
// const PHONE_NUM = "09058562354";
// /** 6 Months */
// import qr5999 from '@/assets/images/qr/bogs/QR-5999.png';
// import qr5799 from '@/assets/images/qr/bogs/QR-5799.png';
// import qr4999 from '@/assets/images/qr/bogs/QR-4999.png';
// /** 3 Months */
// import qr3999 from '@/assets/images/qr/bogs/QR-3999.png';
// import qr3799 from '@/assets/images/qr/bogs/QR-3799.png';
// import qr2999 from '@/assets/images/qr/bogs/QR-2999.png';
// /** 1 Month */
// import qr1999 from '@/assets/images/qr/bogs/QR-1999.png';
// import qr1799 from '@/assets/images/qr/bogs/QR-1799.png';
// import qr999 from '@/assets/images/qr/bogs/QR-999.png';


/******************
 * Mark QR Codes
 */
//  const PHONE_NUM = "09770818567";
// /** 6 Months */
// import qr5999 from '@/assets/images/qr/mark/QR-5999.png';
// import qr5799 from '@/assets/images/qr/mark/QR-5799.png';
// import qr4999 from '@/assets/images/qr/mark/QR-4999.png';
// /** 3 Months */
// import qr3999 from '@/assets/images/qr/mark/QR-3999.png';
// import qr3799 from '@/assets/images/qr/mark/QR-3799.png';
// import qr2999 from '@/assets/images/qr/mark/QR-2999.png';
// /** 1 Month */
// import qr1999 from '@/assets/images/qr/mark/QR-1999.png';
// import qr1799 from '@/assets/images/qr/mark/QR-1799.png';
// import qr999 from '@/assets/images/qr/mark/QR-999.png';




// screenshot 6 months
import sc5999 from '@/assets/images/sc/SC-5999.png';
import sc5799 from '@/assets/images/sc/SC-5799.png';
import sc4999 from '@/assets/images/sc/SC-4999.png';
import scProof5999 from '@/assets/images/sc/SC-PROOF-5999.png';
import scProof5799 from '@/assets/images/sc/SC-PROOF-5799.png';
import scProof4999 from '@/assets/images/sc/SC-PROOF-4999.png';
// screenshot 3 months
import sc3999 from '@/assets/images/sc/SC-3999.png';
import sc3799 from '@/assets/images/sc/SC-3799.png';
import sc2999 from '@/assets/images/sc/SC-2999.png';
import scProof3999 from '@/assets/images/sc/SC-PROOF-3999.png';
import scProof3799 from '@/assets/images/sc/SC-PROOF-3799.png';
import scProof2999 from '@/assets/images/sc/SC-PROOF-2999.png';
// screenshot 1 month
import sc1999 from '@/assets/images/sc/SC-1999.png';
import sc1799 from '@/assets/images/sc/SC-1799.png';
import sc999 from '@/assets/images/sc/SC-999.png';
import scProof1999 from '@/assets/images/sc/SC-PROOF-1999.png';
import scProof1799 from '@/assets/images/sc/SC-PROOF-1799.png';
import scProof999 from '@/assets/images/sc/SC-PROOF-999.png';

// const ORIG_PRICE_QR = 'https://firebasestorage.googleapis.com/v0/b/skilltech-prod.appspot.com/o/images%2Fqrcodes%2Fgcash-qr-4999.png?alt=media&token=1a38b00a-0ecc-44a1-9b9e-6682961c13e3';
// const ORIG_PRICE_SC = 'https://firebasestorage.googleapis.com/v0/b/skilltech-prod.appspot.com/o/images%2Fqrcodes%2Fgcash-sc-4999.png?alt=media&token=cd65b3c4-2563-4554-8cd7-73b19b3206e7';

// const DISC_PRICE_QR = 'https://firebasestorage.googleapis.com/v0/b/skilltech-prod.appspot.com/o/images%2Fqrcodes%2Fgcash-qr-4799.png?alt=media&token=4e902912-2469-4060-8d51-5fe75f3e1825';
// const DISC_PRICE_SC = 'https://firebasestorage.googleapis.com/v0/b/skilltech-prod.appspot.com/o/images%2Fqrcodes%2Fgcash-sc-4799.png?alt=media&token=3886c41c-3f5e-4dca-aa0e-e940be4bed6a';

// const DISC_PRICE_QR_1K_LESS = 'https://firebasestorage.googleapis.com/v0/b/skilltech-prod.appspot.com/o/images%2Fqrcodes%2Fgcash-qr-3999.png?alt=media&token=85c75fa3-42c8-4711-9eb6-0e5103f4d34d';
// const DISC_PRICE_SC_1K_LESS = 'https://firebasestorage.googleapis.com/v0/b/skilltech-prod.appspot.com/o/images%2Fqrcodes%2Fgcash-sc-3999.png?alt=media&token=6095fe5c-7ba0-4fbc-a998-b1f95c7b15e1';


export default {
  props: ["isPurchaseModalOpen", "data", "user", "isCouponValid", "coupon"],
  components: {
    LightBulbIcon, XIcon,
    TransitionRoot,
    TransitionChild,
    Dialog,
    DialogOverlay,
    DialogTitle,
  },
  data(){
    return {
      modalTitle: 'Purchase CAAP Knowledge Reviewer',
      // ui data
      isLoading: false,
      slide_index: 0,
      progress: 0, // progress bar
      readyToExit: false,
      tips: homeData.tips,
      current: 0,
      isLoaded: false,
      refno: "",
      gcashPhoneNum: "",
      qr5999: null,
      qr5799: null,
      qr4999: null,
      qr3999: null,
      qr3799: null,
      qr2999: null,
      qr1999: null,
      qr1799: null,
      qr999: null,
      isFetchingQRCodes: false,
      didNotPayThruGcash: false,
      paymentImage: null,             // File object
      paymentImagePreview: null,      // Base64 or Blob URL for preview
      proofImageURL: null,
    };
  },
  computed: {
    DURATION(){
      /** @todo @junrey refactor the rest */
      if(this.data.selectedMonth == 6){
        return 180;
      }else if(this.data.selectedMonth == 3){
        return 90;
      }else if(this.data.selectedMonth == 1){
        return 30;
      }
    },
    DURATION_IN_WORDS(){
      /** @todo @junrey refactor the rest */
      if(this.data.selectedMonth == 6){
        return '6 Months';
      }else if(this.data.selectedMonth == 3){
        return '3 Months';
      }else if(this.data.selectedMonth == 1){
        return '1 Month';
      }
    },
    ORIG_PRICE_QR(){
      /** @todo @junrey refactor the rest */
      if(this.data.selectedMonth == 6){
        return this.qr5999;
      }else if(this.data.selectedMonth == 3){
        return this.qr3999;
      }else if(this.data.selectedMonth == 1){
        return this.qr1999;
      }
    },
    ORIG_PRICE_SC(){
      if(this.data.finalPrice == 5999){
        return sc5999; // This is the 6 months screenshot
      }else if(this.data.finalPrice == 3999){
        return sc3999; // This is the 3 months screenshot
      }else if(this.data.finalPrice == 1999){
        return sc1999; // This is the 1 month screenshot
      }
    },
    ORIG_PRICE_SC_PROOF(){
      if(this.data.finalPrice == 5999){
        return scProof5999; // This is the 6 months screenshot
      }else if(this.data.finalPrice == 3999){
        return scProof3999; // This is the 3 months screenshot
      }else if(this.data.finalPrice == 1999){
        return scProof1999; // This is the 1 month screenshot
      }
    },
    DISC_PRICE_QR(){
      if(this.data.finalPrice == 5799){
        return this.qr5799; // This is the 6 months qr
      }else if(this.data.finalPrice == 3799){
        return this.qr3799; // This is the 3 months qr
      }else if(this.data.finalPrice == 1799){
        return this.qr1799; // This is the 1 month qr
      }
    },
    DISC_PRICE_SC(){
      if(this.data.finalPrice == 5799){
        return sc5799; // This is the 6 months screenshot
      }else if(this.data.finalPrice == 3799){
        return sc3799; // This is the 3 months screenshot
      }else if(this.data.finalPrice == 1799){
        return sc1799; // This is the 1 month screenshot
      }
    },
    DISC_PRICE_SC_PROOF(){
      if(this.data.finalPrice == 5799){
        return scProof5799; // This is the 6 months screenshot
      }else if(this.data.finalPrice == 3799){
        return scProof3799; // This is the 3 months screenshot
      }else if(this.data.finalPrice == 1799){
        return scProof1799; // This is the 1 month screenshot
      }
    },
    DISC_PRICE_QR_1K_LESS(){
      if(this.data.finalPrice == 4999){
        return this.qr4999; // This is the 6 months qr
      }else if(this.data.finalPrice == 2999){
        return this.qr2999; // This is the 3 months qr
      }else if(this.data.finalPrice == 999){
        return this.qr999; // This is the 1 month qr
      }
    },
    DISC_PRICE_SC_1K_LESS(){
      if(this.data.finalPrice == 4999){
        return sc4999; // This is the 6 months screenshot
      }else if(this.data.finalPrice == 2999){
        return sc2999; // This is the 3 months screenshot
      }else if(this.data.finalPrice == 999){
        return sc999; // This is the 1 month screenshot
      }
    },
    DISC_PRICE_SC_PROOF_1K_LESS(){
      if(this.data.finalPrice == 4999){
        return scProof4999; // This is the 6 months screenshot
      }else if(this.data.finalPrice == 2999){
        return scProof2999; // This is the 3 months screenshot
      }else if(this.data.finalPrice == 999){
        return scProof999; // This is the 1 month screenshot
      }
    },
    isRefNoValid(){
      if (!this.didNotPayThruGcash) {
        if(this.refno.length < 13 ) return false;  // invalid if less than 13 chars
        if(this.refno.length > 13 ) return false;  // invalid if more than 13 chars
        if(isNaN(this.refno)) return false;        // invalid if not a number
      }
      return true;
    },
    hasPaymentImage() {
      if (this.didNotPayThruGcash && !this.paymentImage) return false;

      return true;
    }
  },
  watch:{
    // make sure to remove the value of the ref or the proof image once the user toggle away
    didNotPayThruGcash(newVal, oldVal){
      if (newVal) {
        this.refno = "";
      }else{
        this.paymentImage = null;
        this.paymentImagePreview = null;
      }
    },

    // Every time the modal opens, determine which QR code to use.
    async isPurchaseModalOpen(newVal, oldVal) {
        if (newVal) {

            this.didNotPayThruGcash = false;
            this.paymentImage = null;             // File object
            this.paymentImagePreview = null;      // Base64 or Blob URL for preview
            this.proofImageURL = null;

            this.isFetchingQRCodes = true;

            try {
                const docRef = db.collection("settings").doc("qr_codes");
                const doc = await docRef.get();

                if (doc.exists) {
                    const qrData = doc.data();
                    const activeQrCode = qrData.list.find(qr => qr.isActive) || null; // ✅ Prevent undefined issues

                    if (!activeQrCode) {
                        console.warn("No active QR code found. Using fallback QR codes.");
                        this.assignFallbackQR();
                        return;
                    }

                    const storage = firebase.storage();

                    try {

                      
                        // ✅ Fetch QR Code images from Firebase Storage
                        const qrCodePaths = [
                            `/images/qrcodes/${activeQrCode.storageFolderName}/QR-5999.png`,
                            `/images/qrcodes/${activeQrCode.storageFolderName}/QR-5799.png`,
                            `/images/qrcodes/${activeQrCode.storageFolderName}/QR-4999.png`,
                            `/images/qrcodes/${activeQrCode.storageFolderName}/QR-3999.png`,
                            `/images/qrcodes/${activeQrCode.storageFolderName}/QR-3799.png`,
                            `/images/qrcodes/${activeQrCode.storageFolderName}/QR-2999.png`,
                            `/images/qrcodes/${activeQrCode.storageFolderName}/QR-1999.png`,
                            `/images/qrcodes/${activeQrCode.storageFolderName}/QR-1799.png`,
                            `/images/qrcodes/${activeQrCode.storageFolderName}/QR-999.png`,
                        ];

                        const qrCodeUrls = await Promise.all(
                            qrCodePaths.map(path => storage.ref(path).getDownloadURL().catch(() => null))
                        );

                        // ✅ Preload only non-null images
                        const validUrls = qrCodeUrls.filter(url => url !== null);
                        await this.preloadImages(validUrls);

                        // ✅ Assign the preloaded URLs or use fallback images
                        this.gcashPhoneNum = activeQrCode.gcashPhoneNum || john_PHONE_NUM;
                        this.qr5999 = qrCodeUrls[0] || john_qr5999;
                        this.qr5799 = qrCodeUrls[1] || john_qr5799;
                        this.qr4999 = qrCodeUrls[2] || john_qr4999;
                        this.qr3999 = qrCodeUrls[3] || john_qr3999;
                        this.qr3799 = qrCodeUrls[4] || john_qr3799;
                        this.qr2999 = qrCodeUrls[5] || john_qr2999;
                        this.qr1999 = qrCodeUrls[6] || john_qr1999;
                        this.qr1799 = qrCodeUrls[7] || john_qr1799;
                        this.qr999 = qrCodeUrls[8] || john_qr999;

                        console.log('Fetching of QR codes completed!')
                        
                    } catch (error) {
                        console.error("Error fetching QR code images from Firebase Storage:", error);
                        this.assignFallbackQR();
                    }

                } else {
                    console.warn("QR code document not found. Using fallback QR codes.");
                    this.assignFallbackQR();
                }

            } catch (error) {
                console.error("Error retrieving QR code document from Firestore:", error);
                this.assignFallbackQR();
            } finally {
                this.isFetchingQRCodes = false;
            }
        }
    }
  },
  methods: {
    onImageSelected(event) {
      const file = event.target.files[0];
      if (!file || !file.type.startsWith("image/")) return;

      this.paymentImage = file;
      this.paymentImagePreview = URL.createObjectURL(file); // preview
    },

    removeSelectedImage() {
      this.paymentImage = null;
      this.paymentImagePreview = null;
    },

    /**
     * ✅ Assigns fallback QR codes when data is missing or errors occur.
     */
     assignFallbackQR() {
        this.gcashPhoneNum = john_PHONE_NUM;
        this.qr5999 = john_qr5999;
        this.qr5799 = john_qr5799;
        this.qr4999 = john_qr4999;
        this.qr3999 = john_qr3999;
        this.qr3799 = john_qr3799;
        this.qr2999 = john_qr2999;
        this.qr1999 = john_qr1999;
        this.qr1799 = john_qr1799;
        this.qr999 = john_qr999;
    },
    /**
     * ✅ Preload images to ensure they are fully loaded before assigning them.
     */
    async preloadImages(imageUrls) {
        const loadImage = (url) => {
            return new Promise((resolve) => {
                if (!url) return resolve(null); // Ignore null/undefined URLs
                const img = new Image();
                img.src = url;
                img.onload = () => resolve(url); // Resolve when image loads
                img.onerror = () => resolve(null); // Ignore broken images
            });
        };

        return Promise.all(imageUrls.map(loadImage));
    },

    closePurchaseModal() {
      if(this.slide_index == 4){
        if(!this.readyToExit){
          return
        }else{
          /**
           * We need to reload the window after the user successfully
           * purchased a reviewer. This way, the updated userInfo will be 
           * fetched. They can then access CAAP 101 immediately after they
           * purchased.
           */
          this.$emit("completePurchaseModal");
          // location.reload();
        }
      }
      
      this.$emit("closePurchaseModal");
      setTimeout(() => {
        this.slide_index = 0;  
      }, 500);      
      
    },
    nextStep(index){
      this.slide_index = index;
      document.querySelector("body").scrollTop;
    },
    
    async prepareToOnboard(){
      // let isValidEndorserCode = await userService.isValidEndorserCode(db, this.user.endorser)
      // if(!isValidEndorserCode){
      //   alert("Endorser code not found")
      // }else{
        // this.user.level = (this.isStudent) ? 'Undergraduate' : '';
        // console.log("user: ", this.user)

        if (this.didNotPayThruGcash) {
          
          await this.uploadPaymentImage();

          if (this.proofImageURL == null) {
            alert("Unable to upload the proof of payment image. Please try again later.")
            return;
          }

        }

        
        this.nextStep(4) // navigate to the final slide
        this.onboardUser(); // run loaders and tips
      // }    
    },

    async uploadPaymentImage() {
      try {
        this.proofImageURL = null;

        const storageRef = firebase.storage().ref();
        const filePath = `images/proofs/${this.user.id}-${Date.now()}-${this.paymentImage.name}`;
        const fileRef = storageRef.child(filePath);

        await fileRef.put(this.paymentImage);
        const downloadURL = await fileRef.getDownloadURL();

        // Attach to _caap_data during onboarding
        this.proofImageURL = downloadURL;
      } catch (err) {
        console.error("Error uploading image:", err);
        alert("Failed to upload the proof of payment image. Please try again.");
      }
    },

    completePurchase(){
      this.$router.push({ path: '/reviewers' })  // reroute non-onboarded users
    },

    onboardUser(){
            
      var current_progress = 0;
      var step = 0.05; // the smaller this is the slower the progress bar

      console.log("Processing payment...")

      // run interval for progress bar
      const interval = setInterval( () => {
          current_progress += step;
          this.progress = Math.round(Math.atan(current_progress) / (Math.PI / 2) * 100 * 1000) / 1000
          if (this.progress >= 100){
              clearInterval(interval);
          }else if(this.progress >= 2) {
              step = 1.0
          }
      }, 100);

      // run interval for tips
      const tipsInterval = setInterval(() => {
        this.current = (this.current < this.tips.length-1) ? this.current+1 : 0;
        console.log(this.current)
      }, 4000);
      

      this.user.reviewers.caap = 'paid';

      let _caap_data = {
        purchase_date: moment().valueOf().toString(),
        gcash_refno: this.refno,
        proof_image_url: this.proofImageURL,
      }

      // attach all the coupon data if the user purchased with coupon
      if(this.isCouponValid){
        _caap_data.couponId = this.data.couponId;
        _caap_data.couponCode = this.data.couponCode;
        _caap_data.couponDiscount = this.data.couponDiscount;
        _caap_data.originalPrice = this.data.originalPrice;
        _caap_data.finalPrice = this.data.finalPrice;
        _caap_data.activateLater = this.data.activateLater;
        _caap_data.endorserCode = this.data.endorserCode;
      }else{
        _caap_data.originalPrice = this.data.originalPrice;
        _caap_data.finalPrice = this.data.originalPrice;
        _caap_data.activateLater = true;
      }
      
      _caap_data.duration = this.DURATION;
      _caap_data.durationInWords = this.DURATION_IN_WORDS;

      // Update User Data
      db.collection("users").doc(this.user.id).update({ 
        reviewers: this.user.reviewers,
        caap_data: _caap_data
      })
      .then(async () => { 
        console.log("Successfully updated user") 

        // update coupon usage here.
        if(this.isCouponValid){
          await useCoupon(this.coupon, this.user)
        }
        
        clearInterval(tipsInterval); 
        clearInterval(interval);
        
        // enable buttons and everything
        this.progress = 100; // set progress to 100
        
        setTimeout(() => {
          // add 1 second before removing the loader
          this.readyToExit = true;
        }, 1000)

      })
      .catch((error) => { console.error("Error updating user data: ", error)  });

    },
  },
};
</script>